import { Button, Modal } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { TemplateForm } from "./TemplateForm";
import type { TemplateFormData } from "../../forms/template-schema";
import type { TemplateListItem } from "../../types/template";

interface TemplateActionsProperties {
  /**
   * Callback when a new template is created
   */
  onCreate: (data: TemplateFormData) => Promise<void>;

  /**
   * Callback when an existing template is updated
   */
  onUpdate: (id: string, data: TemplateFormData) => Promise<void>;

  /**
   * Template being edited (if in edit mode)
   */
  editingTemplate: TemplateListItem | null;

  /**
   * Callback to clear editing template
   */
  onClearEdit: () => void;

  /**
   * Whether form is submitting
   */
  isSubmitting?: boolean;
}

/**
 * Action buttons and modals for template management
 *
 * Features:
 * - "New Template" button
 * - Create modal with TemplateForm
 * - Edit modal with TemplateForm
 * - Handles form submission and modal state
 * @param root0
 * @param root0.onCreate
 * @param root0.onUpdate
 * @param root0.editingTemplate
 * @param root0.onClearEdit
 * @param root0.isSubmitting
 * @example
 * ```tsx
 * <TemplateActions
 *   onCreate={async (data) => await createTemplate(data)}
 *   onUpdate={async (id, data) => await updateTemplate(id, data)}
 *   editingTemplate={selectedTemplate}
 *   onClearEdit={() => setSelectedTemplate(null)}
 * />
 * ```
 */
export function TemplateActions({
  onCreate,
  onUpdate,
  editingTemplate,
  onClearEdit,
  isSubmitting = false
}: TemplateActionsProperties) {
  const [createModalOpened, setCreateModalOpened] = useState(false);

  const handleCreate = async (data: TemplateFormData) => {
    await onCreate(data);
    setCreateModalOpened(false);
  };

  const handleUpdate = async (data: TemplateFormData) => {
    if (editingTemplate) {
      await onUpdate(editingTemplate.id, data);
      onClearEdit();
    }
  };

  return (
    <>
      {/* New Template Button */}
      <Button leftSection={<IconPlus size={18} />} onClick={() => setCreateModalOpened(true)}>
        Novo Template
      </Button>

      {/* Create Modal */}
      <Modal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        title="Criar Novo Template"
        size="lg"
        closeOnClickOutside={false}
        closeOnEscape={!isSubmitting}
      >
        <TemplateForm
          onSubmit={handleCreate}
          onCancel={() => setCreateModalOpened(false)}
          isSubmitting={isSubmitting}
          submitLabel="Criar"
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        opened={!!editingTemplate}
        onClose={onClearEdit}
        title="Editar Template"
        size="lg"
        closeOnClickOutside={false}
        closeOnEscape={!isSubmitting}
      >
        {editingTemplate && (
          <TemplateForm
            initialValues={{
              nome: editingTemplate.nome,
              descricao: editingTemplate.descricao,
              componentPath: editingTemplate.componentPath as any,
              ativo: editingTemplate.ativo,
              acessivelConvidada: editingTemplate.acessivelConvidada
            }}
            onSubmit={handleUpdate}
            onCancel={onClearEdit}
            isSubmitting={isSubmitting}
            submitLabel="Atualizar"
          />
        )}
      </Modal>
    </>
  );
}
